import { FC } from 'react';
import { Content } from '@prismicio/client';

import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';
import { TableHead, TableRow, TableWrapper } from '@/components';
import { Simplify } from '../../../prismicio-types';
import { TableContentVariant } from '@/types';

import styles from './threeColumnsTable.module.scss';

const headComponents = (variant: TableContentVariant): JSXMapSerializer => ({
  paragraph: ({ children }) => (
    <p className={`${styles.headText} ${styles[variant]}`}>{children}</p>
  ),
});

const rowTitleComponents = (
  variant: TableContentVariant
): JSXMapSerializer => ({
  paragraph: ({ children }) => (
    <p className={`${styles.rowTitle} ${styles[variant]}`}>{children}</p>
  ),
});

const headMobileTitleComponents = (
  variant: TableContentVariant
): JSXMapSerializer => ({
  paragraph: ({ children }) => (
    <p className={`${styles.headMobileText} ${styles[variant]}`}>{children}</p>
  ),
});

type Props = {
  data: Simplify<Content.PolicyTableSliceThreeColumnsPrimary>;
  variant?: TableContentVariant;
};

export const ThreeColumnsTable: FC<Props> = ({ data, variant = 'page' }) => {
  return (
    <TableWrapper>
      <TableHead className={styles.head}>
        {data.head.map((item, index) => (
          <PrismicRichText
            key={`${item.title[0]?.type}-${index}`}
            field={item.title}
            components={headComponents(variant)}
          />
        ))}
      </TableHead>
      <TableWrapper>
        {data.body.map((item, index) => {
          const hasFirstValue = 'first_value' in item;
          const hasSecondValue = 'second_value' in item;

          return (
            <TableRow
              key={`${item.title[0]?.type}-${index}`}
              className={styles.row}
            >
              <div className={`${styles.box} ${styles[variant]}`}>
                <PrismicRichText
                  key={`${item.title[0]?.type}-${index}`}
                  field={data.head[0]?.title}
                  components={headMobileTitleComponents(variant)}
                />
                <PrismicRichText
                  field={item.title}
                  components={rowTitleComponents(variant)}
                />
              </div>
              <div className={`${styles.values} ${styles[variant]}`}>
                <div className={styles.valueBox}>
                  <PrismicRichText
                    key={`${item.title[1]?.type}-${index}`}
                    field={data.head[1]?.title}
                    components={headMobileTitleComponents(variant)}
                  />
                  {hasFirstValue && (
                    <p className={styles.valueWrapper}>
                      <span
                        className={`${styles.value} ${styles[item.first_value!]}`}
                      >
                        {item.first_value}
                      </span>
                    </p>
                  )}
                </div>
                <div className={styles.valueBox}>
                  <PrismicRichText
                    key={`${item.title[2]?.type}-${index}`}
                    field={data.head[2]?.title}
                    components={headMobileTitleComponents(variant)}
                  />
                  {hasSecondValue && (
                    <p className={styles.valueWrapper}>
                      <span
                        className={`${styles.value} ${styles[item.second_value!]}`}
                      >
                        {item.second_value}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </TableRow>
          );
        })}
      </TableWrapper>
    </TableWrapper>
  );
};
